import * as errors from '../errors'
import { userAuthHeader } from '../apiClient'
import type { IServiceError } from '../errors'
import {
  newRequestId,
  newIdempotencyKey,
  fetchOrThrowNetworkError,
  readErrorBody,
  parseErrorResponse,
  bodyMentions,
  IDEMPOTENCY_KEY_BODY_MISMATCH,
} from './utils'
import { buildPostBody } from './buildPostBody'
import { firstFileProperty } from '~/cynqueModules/utils/hasFileProperty'
import { MAX_UPLOAD_FILE_BYTES } from '~/cynqueModules/uploadLimits'
import {
  pollOperation,
  saveInFlightOperation,
  removeInFlightOperation,
} from './asyncOperation'
import { lostCreateRecoveryPath, recoverLostCreate } from './recoverLostCreate'
import type { INonEntityForPost, TEntityData, TEntityId } from '~/types/models'
import type { IAsyncPostOptions } from '~/types/asyncOperation/models'
import { HTTPStatus } from '~/api/httpStatus'
import { getPublicRuntimeConfig } from '~/cynqueModules/nuxtAccessors'
import { IOverviewForPost } from '~/types/order/models'

type TPostResponse = TEntityData

export async function httpPost<
  T extends TEntityData | IOverviewForPost | INonEntityForPost
>({
  callingMn,
  apiPath,
  entity,
  asyncOptions,
  multipartBody,
}: {
  callingMn: string
  apiPath: string
  entity: T | undefined
  asyncOptions?: IAsyncPostOptions
  /**
   * A body the caller has already built, sent verbatim instead of
   * buildPostBody's. For an endpoint whose multipart shape buildPostBody can't
   * express -- it goes multipart only when a key holds a single File, so a
   * request that is multipart even with no file, or that carries an array of
   * files, has to build its own (services/caseReply.ts). `entity` is still
   * required: the 413 arm reads it.
   */
  multipartBody?: FormData
}): Promise<Awaited<TPostResponse>> {
  const fn = `${callingMn} --> httpPost |`
  if (!apiPath) {
    throw new Error(`${fn} called with falsey apiPath`)
  }
  if (!entity) {
    throw new Error(`${fn} called with falsey entity`)
  }

  const url = `${apiPath}`

  // Note: fetch throws network errors.
  // HTTP status codes > 300 are not network errors.

  // Build the body (JSON, or multipart/form-data when the entity carries a
  // File). See buildPostBody for the multipart contract.
  // A caller-built body is multipart by definition.
  const { body, hasFileObject: isMultipart } = multipartBody
    ? { body: multipartBody, hasFileObject: true }
    : buildPostBody(entity)
  if (isMultipart) {
    console.debug('Has file object')
  }

  const idempotencyKey = asyncOptions?.doAsync ? newIdempotencyKey() : undefined
  const headers: Record<string, string> = Object.assign(
    { 'x-request-id': newRequestId() },
    userAuthHeader()
  )
  // For multipart/form-data (file uploads) the browser must set Content-Type
  // itself so the multipart boundary is included; only set it for JSON bodies.
  if (!isMultipart) {
    headers['content-type'] = 'application/json'
  }
  if (idempotencyKey) {
    headers['Idempotency-Key'] = idempotencyKey
  }
  console.debug(`${fn} Request Id: ${headers['x-request-id']}`)
  if (idempotencyKey) {
    console.debug(`${fn} Idempotency-Key: ${idempotencyKey}`)
  }
  headers['x-app-version'] = getPublicRuntimeConfig().version

  console.debug(`${fn} Sending request POST ${url}`)

  // A transport failure (offline, DNS, reset, CORS, dyno restart) rejects here
  // as a typed NetworkError; HTTP status codes > 300 are NOT network errors and
  // still arrive as a resolved response for the switch below.
  let response: Response
  try {
    response = await fetchOrThrowNetworkError(url, {
      method: 'POST',
      headers,
      body,
    })
  } catch (e) {
    // A NetworkError does not prove the create failed: the response can be lost
    // after the server committed the row. Where the row is findable by the id
    // we minted, look before reporting a failure. See recoverLostCreate.ts.
    const recoveryPath =
      e instanceof errors.NetworkError
        ? lostCreateRecoveryPath({
            apiPath,
            entity,
            isAsync: !!asyncOptions?.doAsync,
            hasCallerBuiltBody: !!multipartBody,
          })
        : undefined
    if (!recoveryPath) {
      throw e
    }
    const row = await recoverLostCreate({
      fn,
      recoveryPath,
      entityId: (entity as { id: TEntityId }).id,
      networkError: e as errors.NetworkError,
    })
    if (row === undefined) {
      throw e
    }
    return row as Awaited<TPostResponse>
  }
  console.debug(`${fn} POST Response:`, response)
  switch (response.status) {
    case HTTPStatus.OK:
    case HTTPStatus.Created:
      const responseBody = await response.json()
      return responseBody.data
    case HTTPStatus.Accepted: {
      const acceptedBody = await response.json()
      const operationId = acceptedBody.data.operation_id
      console.debug(`${fn} Async operation accepted: ${operationId}`)
      saveInFlightOperation(operationId, apiPath)
      try {
        const result = await pollOperation(
          operationId,
          callingMn,
          asyncOptions?.onPending
        )
        removeInFlightOperation(operationId)
        return result
      } catch (e) {
        removeInFlightOperation(operationId)
        throw e
      }
    }
    // 400 and 422 both mean "the payload was rejected" and share one type.
    // They used to share a case with 405, which is a different thing entirely:
    // a 405 is a routing/programming error, not a rejected body, so it stays a
    // plain ServiceError below.
    case HTTPStatus.BadRequest:
    case HTTPStatus.UnprocessableEntity: {
      const { message, body } = await parseErrorResponse(response)
      if (
        asyncOptions?.doAsync &&
        bodyMentions(body, IDEMPOTENCY_KEY_BODY_MISMATCH)
      ) {
        console.warn(
          `${fn} Idempotency-Key body mismatch — retrying with fresh key`
        )
        return httpPost({
          callingMn,
          apiPath,
          entity,
          asyncOptions,
          multipartBody,
        })
      }
      console.log(`${fn} rejected (${response.status}): ${message}`, body)
      throw new errors.ValidationError(
        response.status,
        'POST',
        url,
        message,
        body
      )
    }
    case HTTPStatus.MethodNotAllowed: {
      const { message } = await parseErrorResponse(response)
      console.log(`${fn} method not allowed: ${message}`)
      throw new errors.ServiceError(response.status, message)
    }
    // The request body exceeded the API's cap. Litestar checks the
    // content-length header and rejects before reading a byte, so this arrives
    // only after the whole file has been sent -- which is why the real guard is
    // the `maxBytes` rule at pick time, and reaching here means the client cap
    // has drifted above the API's.
    case HTTPStatus.PayloadTooLarge: {
      const { body } = await parseErrorResponse(response)
      // buildPostBody has already collapsed the entity into an opaque FormData,
      // but `entity` itself still carries the File, so the message can name it.
      const file = firstFileProperty(entity)
      console.log(`${fn} upload rejected as too large (413)`, body)
      throw new errors.PayloadTooLargeError(
        'POST',
        url,
        MAX_UPLOAD_FILE_BYTES,
        file,
        body
      )
    }
    // A create that collides with an existing row (a unique constraint, or a
    // second POST for a record the first one already made).
    case HTTPStatus.Conflict:
      throw new errors.UpdateConflictError(
        'POST',
        url,
        await readErrorBody(response)
      )
    default: {
      // Was a bare `await response.json()`, which REJECTS on an empty or
      // non-JSON body (an HTML proxy page, a 204) — replacing the real failure
      // with a SyntaxError at the moment we are trying to describe it.
      const { message, body } = await parseErrorResponse(response)
      throw new errors.APIUnhandledStatus(response.status, {
        statusCode: response.status,
        message,
        body,
      } as unknown as IServiceError)
    }
  }
}
